import React, {
  createContext,
  useEffect,
  useReducer,
} from 'react';
import type { FC } from 'react';
import jwtDecode from 'jwt-decode';
import { useDispatch, useSelector } from 'src/store';
import { setUserAuthenticated } from 'src/slices/auth';
import SplashScreen from 'src/components/SplashScreen';
import { urls } from 'src/utils/api';
import {
  protectedAxios as axios,
  isValidToken,
  getFreshAccess,
  setSession,
} from 'src/utils/auth';
import type {
  Action,
  AuthState,
  AuthContextValue,
  AuthProviderProps,
  JWTToken
} from './types';

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  logout: () => {},
});

const rolesMap = {
  user: 'Пользователь',
  guest: 'Гость',
  superadmin: 'Суперпользователь',
  admin: 'Администратор'
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const reduxDispatch = useDispatch();
  const { userAuthenticated } = useSelector((reduxState) => reduxState.auth);

  const login = async (username: string, password: string) => {
    const response = await axios.post<{
      refresh: string;
      access: string;
    }>(urls.token, { username, password });

    const {
      refresh: refreshToken,
      access: accessToken,
    } = response.data;

    setSession(accessToken, refreshToken);

    const decodedToken = jwtDecode<JWTToken>(accessToken);

    const {
      user_id: userId,
      username: tokenUsername,
      first_name: firstName,
      last_name: lastName,
      role: userRole,
      full_access: fullAccess
    } = decodedToken;

    dispatch({
      type: 'LOGIN',
      payload: {
        user: {
          id: userId,
          name: (lastName && firstName)
            ? `${lastName} ${firstName}`
            : tokenUsername ?? 'Пользователь',
          role: userRole ? rolesMap[userRole] : null,
          hasWriteAccess: fullAccess
        }
      },
    });

    reduxDispatch(setUserAuthenticated({ authenticated: true }));
  };

  const logout = () => {
    setSession(null, null);
    dispatch({ type: 'LOGOUT' });
    reduxDispatch(setUserAuthenticated({ authenticated: false }));
  };

  useEffect(() => {
    const initialise = async () => {
      try {
        let accessToken = window.localStorage.getItem('accessToken');

        if (accessToken) {
          let refreshToken = window.localStorage.getItem('refreshToken');

          if (!isValidToken(accessToken)) {
            const [
              newAccessToken,
              newRefreshToken,
            ] = await getFreshAccess(`${refreshToken}`);

            accessToken = newAccessToken;
            refreshToken = newRefreshToken;
          }

          setSession(accessToken, refreshToken);

          const decodedToken = jwtDecode<JWTToken>(accessToken);

          const {
            user_id: userId,
            username: tokenUsername,
            first_name: firstName,
            last_name: lastName,
            role: userRole,
            full_access: fullAccess,
          } = decodedToken;

          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: true,
              user: {
                id: userId,
                name: (lastName && firstName)
                  ? `${lastName} ${firstName}`
                  : tokenUsername ?? 'Пользователь',
                role: rolesMap[userRole],
                hasWriteAccess: fullAccess
              }
            },
          });

          reduxDispatch(setUserAuthenticated({ authenticated: true }));
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });

          reduxDispatch(setUserAuthenticated({ authenticated: false }));
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });

        reduxDispatch(setUserAuthenticated({ authenticated: false }));
      }
    };

    initialise();
  }, []);

  useEffect(() => {
    if (!userAuthenticated) {
      setSession(null, null);
      dispatch({ type: 'LOGOUT' });
    }
  }, [userAuthenticated]);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
